// Service: orders.js
// Purpose: API methods for order listing, cancellation, and modify via OMS (gateway-routed, auth:8000)

import api from '../../services/api';

const OrdersService = {
  /**
   * List orders with optional filters.
   *
   * fromBarStart / toBarStart are ISO-8601 timestamps filtering on the order's
   * bar-clock (NOT wall-clock). Use these to restrict results to the chart's
   * visible time window — during simulation replay a strategy instance can
   * have thousands of orders spanning months, most outside the visible range.
   *
   * @param {Object} params
   * @param {string} [params.strategyInstanceId] - Filter by strategy instance
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.fromBarStart] - ISO-8601, inclusive lower bound on bar_start
   * @param {string} [params.toBarStart] - ISO-8601, inclusive upper bound on bar_start
   * @param {number} [params.limit=100] - Max results
   * @param {number} [params.skip=0] - Offset
   */
  getOrders: async ({ strategyInstanceId, status, fromBarStart, toBarStart, limit = 100, skip = 0 } = {}) => {
    return api.get('/orders', {
      params: {
        strategy_instance_id: strategyInstanceId || undefined,
        status: status || undefined,
        from_bar_start: fromBarStart || undefined,
        to_bar_start: toBarStart || undefined,
        limit,
        skip,
      },
    });
  },

  /**
   * Cancel a pending/open order
   * @param {string} orderId - UUID of order to cancel
   */
  cancelOrder: async (orderId) => {
    return api.post(`/orders/${orderId}/cancel`);
  },

  /**
   * Fetch a single order's current state (R5.3/R6.2 pre-fill/refresh source).
   * Response includes the derived `pending_corrective_type` ("CANCEL" | "MODIFY" | null)
   * so the FE can distinguish an unresolved-corrective row from a genuinely unchanged one.
   * @param {string} orderId - UUID of the order
   */
  getOrderById: async (orderId) => {
    return api.get(`/orders/${orderId}`);
  },

  /**
   * #10474 — per-order lifecycle timeline for the OrderDetailDrawer.
   * Response `data`: { order_id, execution_mode, events: [{key,event_type,label,kind,at,clock,source}] }
   * — ONE merged, time-ordered list: processed_events transitions (accepted / ack / amend /
   * amend-rejected / OPEN / partial / fill / cancel / reject) plus the order's pre-broker column
   * stamps where a producer wrote them. `clock` is 'broker' when `at` is the authoritative
   * broker/exchange stamp (routed_at, exchange_fill_timestamp), else 'oms' (projector time).
   * Read-only; ownership-scoped like getOrderById.
   * @param {string} orderId - UUID of the order
   */
  getOrderTimeline: async (orderId) => {
    return api.get(`/orders/${orderId}/timeline`);
  },

  /**
   * Fetch the modify-capability descriptor for this order's routed broker (R6.5/R6.7).
   * Shape: `{"mode":"BROKER","order_types":[{canonical,display_name,definition,raw_term}, ...],
   *          "validities":[...], "disclosed_quantity_supported": bool}`
   * or, for a never-routed MONITORING order: `{"mode":"OMS_INTERNAL","editable_fields":[...]}`.
   * @param {string} orderId - UUID of the order
   */
  getModifyCapabilities: async (orderId) => {
    return api.get(`/orders/${orderId}/modify-capabilities`);
  },

  /**
   * Submit a modify request. `fields` carries CANONICAL values only (e.g. order_type: "SL",
   * validity: "EOS") — never a broker-native raw term; the OMS translates canonical->raw at
   * dispatch time. Only send the fields the operator actually changed.
   * @param {string} orderId - UUID of the order
   * @param {Object} fields - e.g. { qty, limit_price, trigger_price, order_type, validity, disclosed_quantity }
   */
  modifyOrder: async (orderId, fields) => {
    return api.put(`/orders/${orderId}/modify`, fields);
  },

  /**
   * R8.6-R8.10 — fetch rejected-signal rows (OMS-outcome enrichment for the Signal
   * Evaluation panel). Response shape: { rejected_signals: [{signal_id, reject_reason,
   * sub_reason, created_at, matched, matched_orders: [{order_id, status, account_id,
   * execution_mode, created_at}]}], oms_degraded: bool }. `execution_mode` on each
   * matched_orders element is already server-derived ("live"|"paper"|"unattributed") —
   * never re-derive it client-side.
   * @param {string} strategyInstanceId
   */
  getRejectedSignals: async (strategyInstanceId) => {
    return api.get('/orders/rejected', {
      params: { strategy_instance_id: strategyInstanceId },
    });
  },
};

export default OrdersService;
